import { useState, type CSSProperties } from 'react';
import type { TimelineCard } from '@hitster/shared';
import { playerColor } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import { IconPlus } from './Icon.tsx';

/**
 * Die Zeitleiste eines Spielers.
 *
 * Aufbau als durchgehendes Band statt als Kachelreihe: Eine waagerechte
 * Schiene verbindet die Karten, jede sitzt mit einem Punkt darauf. Das
 * ist es, was eine Zeitleiste ausmacht -- nebeneinandergelegte Kacheln
 * zeigen nur eine Reihe, keinen Verlauf.
 *
 * Ein "Slot" mit Index i bedeutet: die Karte kommt zwischen cards[i-1]
 * und cards[i]. Es gibt also immer genau `cards.length + 1` Slots.
 * Diese Zaehlweise ist identisch zur Regel-Engine im Server -- so kann
 * der Index unveraendert uebertragen werden.
 */

export interface TimelineProps {
  cards: TimelineCard[];
  /** Slots anklickbar machen. */
  interactive?: boolean;
  /** Aktuell gewaehlter Slot, null = keiner. */
  selectedSlot?: number | null;
  onSelectSlot?: (slotIndex: number) => void;
  /** Karte, die neu dazugekommen ist -- wird hervorgehoben. */
  highlightCardId?: string | null;
  /**
   * Platzsparende Darstellung fuer fremde Zeitleisten.
   *
   * Kleineres Cover, kein Titel, kein Interpret -- die Angaben stehen
   * im `title` der Karte. Hiess frueher `showCovers`, was seit dem
   * kleinen Cover in der Kompaktansicht das Gegenteil behauptete.
   */
  compact?: boolean;
  /**
   * Laeuft gerade ein Song?
   *
   * Dann atmet ein weicher Lichthof hinter der Leiste. Das ersetzt einen
   * Fortschrittsbalken durch etwas, das man nebenbei wahrnimmt -- und
   * kommt beim Aufdecken zur Ruhe.
   */
  live?: boolean;
  /**
   * Wem gehoert diese Leiste?
   *
   * Faerbt Schiene und Punkte in der Spielerfarbe. Auf dem Fernseher
   * stehen bis zu sechs Leisten untereinander -- eine durchgehende
   * Linie ordnet sie auf drei Metern zu, wo ein Name nicht mehr lesbar
   * ist.
   *
   * Ohne Angabe bleibt die Leiste neutral.
   */
  ownerId?: string | null;
}

export function Timeline({
  cards,
  interactive = false,
  selectedSlot = null,
  onSelectSlot,
  highlightCardId = null,
  compact = false,
  live = false,
  ownerId = null,
}: TimelineProps) {
  const t = useT();

  /**
   * Karte, deren Angaben gerade unter der Leiste stehen.
   *
   * Nur fuer die Kompaktansicht: dort sind Titel und Interpret
   * ausgeblendet, weil bei 74px Kartenbreite kein lesbarer Text
   * hinpasst.
   */
  const [detail, setDetail] = useState<TimelineCard | null>(null);

  const renderSlot = (slotIndex: number) => {
    const isSelected = selectedSlot === slotIndex;
    const label =
      slotIndex === 0
        ? t('game.beforeAll')
        : slotIndex === cards.length
          ? t('game.afterAll')
          : t('game.placeHere');

    return (
      <button
        key={`slot-${slotIndex}`}
        type="button"
        className={[
          'tl-slot',
          interactive ? 'tl-slot--active' : '',
          isSelected ? 'tl-slot--selected' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        disabled={!interactive}
        onClick={() => onSelectSlot?.(slotIndex)}
        aria-label={label}
        aria-pressed={isSelected}
      >
        <span className="tl-slot__marker" aria-hidden="true">
          {/* Bei gewaehltem Slot zeigt CSS ein "?" -- die Karte ist geheim. */}
          {!isSelected && interactive && <IconPlus size="0.9rem" />}
        </span>
      </button>
    );
  };

  return (
    <div
      className={`timeline-wrap ${live ? 'timeline-wrap--live' : ''}`}
      style={ownerId ? ({ '--player': playerColor(ownerId) } as CSSProperties) : undefined}
    >
      <div
        className={`timeline ${compact ? 'timeline--compact' : ''}`}
        role="list"
        aria-label={t('game.timeline')}
      >
        {/* Die Schiene laeuft durch und verbindet alle Punkte. */}
        <span className="timeline__rail" aria-hidden="true" />

        {renderSlot(0)}

        {cards.map((card, index) => (
          <div className="tl-group" key={card.cardId}>
            <div
              className={`tl-card ${card.cardId === highlightCardId ? 'tl-card--new' : ''}`}
              role="listitem"
              /*
               * Nur kompakt anfassbar. In der vollen Ansicht stehen
               * Titel und Interpret ohnehin darunter -- ein
               * Fokusstopp pro Karte waere dort nur Tastaturballast.
               */
              tabIndex={compact ? 0 : undefined}
              onMouseEnter={compact ? () => setDetail(card) : undefined}
              onMouseLeave={compact ? () => setDetail(null) : undefined}
              onFocus={compact ? () => setDetail(card) : undefined}
              onBlur={compact ? () => setDetail(null) : undefined}
              /* Ohne Zeigegeraet gibt es kein Hover -- Tippen muss reichen. */
              onClick={
                compact
                  ? () => setDetail((current) => (current?.cardId === card.cardId ? null : card))
                  : undefined
              }
              /*
               * Volltext am ganzen Kaertchen, nicht nur am Titel.
               *
               * In der Kompaktansicht sind Titel und Interpret
               * ausgeblendet -- ein `title` nur auf dem versteckten
               * Element waere unerreichbar. So kommt man ueberall an
               * die Angaben, ohne dass sie Platz kosten.
               */
              title={`${card.title} — ${card.artists.join(', ')} (${card.year})`}
            >
              {/*
               * Das Cover erscheint IMMER, auch kompakt.
               *
               * Es hing frueher an `showCovers`, wodurch fremde
               * Zeitleisten nur noch aus Jahreszahlen bestanden. Das
               * Bild ist aber die einzige schnelle Wiedererkennung --
               * und es kostet keine Breite, nur Hoehe: die Karte ist
               * eine Spalte, das Cover sitzt oben drin.
               */}
              <span className="tl-card__art">
                {card.coverUrl ? (
                  <img className="tl-card__cover" src={card.coverUrl} alt="" loading="lazy" />
                ) : (
                  <span className="tl-card__cover" />
                )}
              </span>

              {/* Der Punkt sitzt auf der Schiene und bindet die Karte an. */}
              <span className="tl-card__node" aria-hidden="true" />

              <span className="tl-card__year">{card.year}</span>

              <span className="tl-card__meta">
                <span className="tl-card__title" title={card.title}>
                  {card.title}
                </span>
                <span className="tl-card__artist" title={card.artists.join(', ')}>
                  {card.artists.join(', ')}
                </span>
              </span>
            </div>

            {renderSlot(index + 1)}
          </div>
        ))}

        {cards.length === 0 && <span className="timeline__empty">{t('game.timelineEmpty')}</span>}
      </div>

      {/*
       * Angaben zur angetippten Karte — UNTERHALB der Scrollflaeche.
       *
       * Ein schwebender Tooltip waere hier nicht moeglich: `.timeline`
       * hat `overflow-y: hidden` (noetig, damit neben dem waagerechten
       * kein zweiter Balken erscheint), und alles, was ueber oder unter
       * die Karte ragt, wuerde abgeschnitten.
       *
       * Die Zeile ist immer da, auch leer. Erschiene sie erst beim
       * Zeigen, ruckte die ganze Leiste bei jeder Mausbewegung.
       */}
      {compact && (
        <div className="timeline__detail" aria-live="polite">
          {detail && (
            <>
              <span className="timeline__detail-title">{detail.title}</span>
              <span className="timeline__detail-artist">{detail.artists.join(', ')}</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
